/* The active plan card, one drawing for two places.
 *
 * The declarations are the client detail summary column's — KANON v3 — moved here on 1 September
 * 2026 when the owner ruled that the roster preview draws the same card. The roster's own copies
 * are DELETED rather than left beside these: two stylesheets carrying one card is how two surfaces
 * learn to disagree, and in a production build the chunk order decides which copy wins.
 *
 * There is ONE cover height for both places since the column was compressed on 1 September 2026 —
 * the summary column's 132px was the single biggest block of height in a column that had to fit a
 * laptop, and the roster panel's own 112px was already close to the 88 they share now. `.panel`
 * therefore carries no size override at the moment; it stays as the seam the size prop names,
 * because the two surfaces have different room and the next round may need it again. */

.card {
  /* The containing block for `.hit`. `.cover` is positioned too, so an overlay stretched from
   * inside the cover would reach the cover's box and stop — which is why the link is a child of
   * the card rather than of the hero text it names. */
  position: relative;
  display: flex;
  flex: none;
  flex-direction: column;
  overflow: hidden;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  transition-duration: var(--dur-fast);
  transition-property: border-color;
  transition-timing-function: var(--ease);
}

/* The whole card as one link: an overlay, the same mechanism the roster row uses for the same
 * reason. `border-radius` so the focus ring and the pointer stop at the card's own corners. */
.hit {
  position: absolute;
  inset: var(--space-0);
  border-radius: var(--radius-md);
  cursor: pointer;
}

/* Hover changes the EDGE and underlines the name (below, where `.name` is declared). Nothing moves
 * in Z — lifting a card on hover is the dated gesture the view ADR names — and the underline is the
 * second channel, so the affordance is never carried by a colour alone. */
.card:has(.hit:hover) {
  border-color: var(--border-2);
}

/* The ring is drawn on the CARD, not on the overlay: `overflow: hidden` clips a descendant's
 * outline and does not clip the element's own. Same two declarations every focusable control in
 * this app wears. */
.card:has(.hit:focus-visible) {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-2px);
}

/* Geometry only. The photo's treatment — its ground, its position and its scale — is
 * `photoBox`'s `.painted`, applied as a second class in the TSX. The `height` here is what makes
 * the card the same 172px in every state: a background image contributes nothing to layout, so a
 * cover that arrives late, arrives never, or was never set moves nothing at all (measured
 * 2 September 2026: 88px cover, 172px card, in every frame of a delayed run and a blocked one). */
.cover {
  position: relative;
  display: flex;
  flex: none;
  align-items: flex-end;
  height: var(--h-plan-cover);
  padding: var(--space-13px) var(--space-4) var(--space-14px);
  overflow: hidden;
}

.scrim {
  position: absolute;
  inset: var(--space-0);
  display: block;
  background: var(--scrim-cover);
}

.heroText {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

/* The category, in the trainer's language and in the plan library's own hue — the key picks it
 * through `planCategoryColour`. The dot repeats the hue so the chip is not colour alone. */
.chip {
  display: inline-flex;
  gap: var(--space-6px);
  align-self: flex-start;
  align-items: center;
  height: var(--h-detail-pill);
  padding: 0 var(--space-10px);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-on-photo);
  letter-spacing: var(--ls-cat);
  white-space: nowrap;
  background: var(--wash-on-photo);
  border-radius: var(--r-pill);
}

.chipDot {
  display: block;
  flex: none;
  width: var(--space-6px);
  height: var(--space-6px);
  background: var(--cat-ink);
  border-radius: var(--r-circle);
}

.name {
  overflow: hidden;
  font-size: var(--fs-plan-hero);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-plan-hero);
  color: var(--text-on-photo);
  letter-spacing: var(--ls-plan-title);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The hover's second channel. It sits here rather than beside the card's own hover rule so the
 * plain `.name` declaration comes first — `no-descending-specificity`. */
.card:has(.hit:hover) .name {
  text-decoration: underline;
}

.mode {
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2-on-photo);
  white-space: nowrap;
}

.body {
  display: flex;
  flex-direction: column;
  gap: var(--space-9px);
  padding: var(--space-15px) var(--space-4);
}

.eyebrow {
  overflow: hidden;
  font-size: var(--fs-badge);
  font-style: normal;
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-3);
  letter-spacing: var(--ls-lab);
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.weeks {
  display: flex;
  gap: var(--space-2);
  align-items: baseline;
  margin: var(--space-0);
}

.weeksValue {
  flex: 1;
  min-width: 0;
  font-size: var(--fs-exercise);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  color: var(--text);
  letter-spacing: var(--ls-name);
  font-variant-numeric: tabular-nums;
}

/* The horizon, and the pause that replaces it. One class for both: they stand in the same place and
 * answer the same question — when does this end — and a paused plan answers it with "wstrzymany". */
.paused {
  flex: none;
  font-size: var(--fs-caption);
  font-style: normal;
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-3);
  white-space: nowrap;
}

.track {
  display: block;
  height: var(--space-6px);
  overflow: hidden;
  background: var(--bg-sunken);
  border-radius: var(--radius-4px);
}

.fill {
  display: block;
  height: 100%;
  background: var(--accent);
  border-radius: var(--radius-4px);
}

/* ---- The summary column's size (KANON „Karty zadań" `planColumnCard`, 24 September 2026) ----
 * The photograph runs taller and the category stands in its top-left corner, off the name, so the
 * cover reads top-down: what kind of plan, then which one. The roster panel keeps the shared 88. */
.column .cover {
  height: var(--h-plan-cover-column);
}

/* The hero text fills the cover, so the chip's corner is the cover's own (inside its padding). */
.column .heroText {
  flex: 1;
  align-self: stretch;
  justify-content: flex-end;
}

.column .chip {
  position: absolute;
  inset-block-start: var(--space-0);
  inset-inline-start: var(--space-0);
}

.column .body {
  gap: var(--space-2);
}

/* `--text-2`: `--text-3` at the micro size measured 3.0:1 on the card. */
.endDate {
  align-self: flex-end;
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}
